import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Motion from '@/components/base/Motion'
import Typography from '@/components/base/Typography'
import TypographyMask from '@/components/base/TypographyMask'

import { opacityReveal } from './animations/opacity'
import { cn } from '@/lib/utils'
import { WPImage, WPLink } from '@/types'

export type DoubleBlockProps = HTMLAttributes<HTMLElement> &
  Partial<{
    image: WPImage
    pretitle: string
    title: string
    description: string
    link: WPLink
    alignment: 'left' | 'right'
    image_cover?: boolean
  }>

const DoubleBlock = ({
  image,
  pretitle,
  title,
  description,
  link,
  image_cover,
  alignment
}: DoubleBlockProps) => {
  return (
    <Motion animation={opacityReveal}>
      <section data-double-block={alignment}>
        <Container>
          <Grid>
            <div
              className={cn(
                'relative col-span-4',
                'md:col-span-6',
                'lg:col-span-6',
                'h-auto w-full',
                'rounded-[15px]',
                'overflow-hidden'
              )}
            >
              {image ? (
                <img
                  src={image.url}
                  alt={title}
                  width={image.width}
                  height={image.height}
                  className={
                    image_cover ? 'h-full w-full object-cover' : 'h-auto w-full'
                  }
                />
              ) : null}
            </div>

            <div
              className={cn(
                'col-span-4 flex flex-col justify-between gap-[15px] rounded-[15px] bg-[#F0F2F5] p-[30px] md:col-span-6 lg:col-span-6',
                {
                  'lg:-order-1': alignment === 'right'
                }
              )}
            >
              <div className="flex flex-col gap-[15px]">
                {pretitle ? (
                  <Typography variant="label">{pretitle}</Typography>
                ) : null}
                {title ? (
                  <Typography variant="h3" data-double-block-title>
                    {title}
                  </Typography>
                ) : null}
                {description ? (
                  <Typography variant="body" data-double-block-description>
                    {description}
                  </Typography>
                ) : null}
              </div>
              {link ? (
                <Button variant="primary" asChild>
                  <Link
                    href={link?.url || ''}
                    className={cn('mt-[30px] self-start')}
                    data-double-block-link
                    scroll={false}
                  >
                    <TypographyMask>
                      <span>{link.title}</span>
                    </TypographyMask>
                  </Link>
                </Button>
              ) : null}
            </div>
          </Grid>
        </Container>
      </section>
    </Motion>
  )
}

export default DoubleBlock
